import { useState } from "react";
import {
  Button,
  Card,
  Descriptions,
  Field,
  Icon,
  Input,
  Segmented,
  Space,
  Tag,
  Text,
  useTheme,
  useToast,
} from "@app/ui";
import { api } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import { useErrorText } from "../errors/index.js";
import { useT, useFormatDateTime, useLang, type Lang } from "../i18n.js";
import { LangFlag } from "./flags.js";
import type { PublicUser } from "../types.js";

/**
 * Account tab: the editable identity, the read-only facts behind it, the
 * device-local preferences, and the GDPR export. Security lives in its own tab.
 */

export function AccountTab({
  user,
  onDownloadData,
  downloading,
}: {
  user: PublicUser;
  onDownloadData: () => void;
  downloading: boolean;
}) {
  const t = useT();
  const toast = useToast();
  const errorText = useErrorText();
  const fmtDate = useFormatDateTime();
  const { refreshMe } = useAuth();
  const { lang, setLang } = useLang();
  const { theme, setTheme } = useTheme();

  const [name, setName] = useState(user.displayName ?? "");
  const [saving, setSaving] = useState(false);

  const dirty = name.trim() !== "" && name.trim() !== (user.displayName ?? "");

  const saveName = async () => {
    setSaving(true);
    try {
      await api<void>("/auth/profile", { method: "POST", body: { displayName: name.trim() } });
      await refreshMe();
      toast.success(t("profile.saved"));
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size="lg" className="w-full">
      <Card title={t("profile.identity")}>
        <div className="pf-form">
          <Field label={t("profile.displayName")} help={t("profile.displayNameHint")}>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              onKeyDown={(e) => {
                if (e.key === "Enter" && dirty) void saveName();
              }}
            />
          </Field>
          <div>
            <Button
              variant="primary"
              size="sm"
              loading={saving}
              disabled={!dirty}
              onClick={() => void saveName()}
            >
              {t("common.save")}
            </Button>
          </div>
        </div>
      </Card>

      <Card title={t("profile.accountInfo")}>
        <Descriptions
          column={2}
          items={[
            {
              key: "email",
              label: t("common.email"),
              children: (
                <span className="inline-flex flex-wrap items-center gap-2">
                  {user.email}
                  <Tag color={user.emailVerified ? "success" : "warning"}>
                    {t(user.emailVerified ? "profile.emailVerified" : "profile.emailUnverified")}
                  </Tag>
                </span>
              ),
            },
            {
              key: "id",
              label: t("profile.userId"),
              children: (
                <Text code copyable small>
                  {user.id}
                </Text>
              ),
            },
            { key: "roles", label: t("common.roles"), children: user.roles.join(", ") || "—" },
            {
              key: "perms",
              label: t("profile.permissions"),
              children: t("profile.permissionsCount", { count: user.permissions.length }),
            },
            { key: "since", label: t("common.createdAt"), children: fmtDate(user.createdAt) },
            {
              key: "mfa",
              label: t("common.mfa"),
              children: (
                <Tag color={user.mfaEnabled ? "success" : "warning"}>
                  {t(user.mfaEnabled ? "dashboard.mfaEnabled" : "dashboard.mfaDisabled")}
                </Tag>
              ),
            },
          ]}
        />
      </Card>

      <Card title={t("profile.preferences")}>
        <Space direction="vertical" size="md" className="w-full">
          <Text small secondary>
            {t("profile.preferencesHint")}
          </Text>
          {/* self-start: Field stacks its children in a column, so a segmented
              control would otherwise stretch to the full card width. */}
          <div className="pf-grid">
            <Field label={t("common.language")}>
              <Segmented<Lang>
                className="self-start"
                label={t("common.language")}
                size="md"
                options={[
                  { value: "en", label: "English", icon: <LangFlag lang="en" /> },
                  { value: "tr", label: "Türkçe", icon: <LangFlag lang="tr" /> },
                ]}
                value={lang}
                onChange={setLang}
              />
            </Field>
            <Field label={t("profile.theme")}>
              <Segmented<"light" | "dark">
                className="self-start"
                label={t("profile.theme")}
                size="md"
                options={[
                  { value: "light", label: t("profile.themeLight"), icon: <Icon name="sun" size={14} /> },
                  { value: "dark", label: t("profile.themeDark"), icon: <Icon name="moon" size={14} /> },
                ]}
                value={theme}
                onChange={setTheme}
              />
            </Field>
          </div>
        </Space>
      </Card>

      <Card title={t("profile.privacy")}>
        <Space direction="vertical" size="md" className="w-full">
          <Text small secondary>
            {t("profile.privacyHint")}
          </Text>
          <div>
            <Button
              size="sm"
              icon={<Icon name="download" size={14} />}
              loading={downloading}
              onClick={onDownloadData}
            >
              {t("dashboard.downloadData")}
            </Button>
          </div>
        </Space>
      </Card>
    </Space>
  );
}
